import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalBasicComponent } from '../common/dialogs/modal-basic/modal-basic.component';
import { InterfazAuditoriaResponse } from '../../models/interfaz-auditoria/interfaz-auditoria-response';

export type AuditoriaClasificacion = 'AUD' | 'NRAM' | 'TNF' | 'NCP';
interface AuditoriaStatusConfig {
  icono: string;
  color: string;
}

@Component({
  selector: 'app-interfaz-auditoria',
  templateUrl: './interfaz-auditoria.component.html',
  styleUrls: ['./interfaz-auditoria.component.css'],
})
export class InterfazAuditoriaComponent {
  @Input() resultados: InterfazAuditoriaResponse[] = [];
  @Input() decisionInicial: 'si' | 'no' | null = null;
  @Input() readonly = false;

  @Output() anuladoExitoso = new EventEmitter<void>();
  @Output() decisionCambio = new EventEmitter<'si' | 'no' | null>();
  @Output() continuarSiguiente = new EventEmitter<void>();

  @ViewChild('confirmNoTemplate') confirmNoTemplate!: TemplateRef<any>;

  selectedDecision: 'si' | 'no' | null = null;
  anuladoMensaje = '';
  anuladoErrorMensaje = '';
  anuladoConfirmado = false;
  mensajeErrorValidacion = '';
  sortColumn: keyof InterfazAuditoriaResponse | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  simularErrorRadicado = false; // UI-only: bandera para disparar flujo de error en demo

  readonly statusConfig: Record<AuditoriaClasificacion, AuditoriaStatusConfig> =
    {
      AUD: {
        icono: 'autorenew',
        color: '#0d6efd',
      },
      NRAM: {
        icono: 'check_circle',
        color: '#2ecc71',
      },
      TNF: {
        icono: 'close',
        color: '#e63946',
      },
      NCP: {
        icono: 'block',
        color: '#95a5a6',
      },
    };

  constructor(private modalService: NgbModal) {}

  ngOnInit(): void {
    if (this.decisionInicial === 'si' || this.decisionInicial === 'no') {
      this.selectedDecision = this.decisionInicial;
    }
  }

  get dataParaMostrar(): InterfazAuditoriaResponse[] {
    const base =
      this.resultados && this.resultados.length > 0 ? [...this.resultados] : [];
    return this.sortData(base);
  }

  get total(): number {
    return this.dataParaMostrar.length;
  }

  get totalNoRequiere(): number {
    return this.dataParaMostrar.filter((r) => r.resultado.codigo === 'NRAM')
      .length;
  }

  get totalNoFactible(): number {
    return this.dataParaMostrar.filter((r) => r.resultado.codigo === 'TNF')
      .length;
  }

  get totalNoCumple(): number {
    return this.dataParaMostrar.filter((r) => r.resultado.codigo === 'NCP')
      .length;
  }

  get allNoRequiere(): boolean {
    return this.total > 0 && this.totalNoRequiere === this.total;
  }

  get allNoFactibleOCumple(): boolean {
    return (
      this.total > 0 && this.totalNoFactible + this.totalNoCumple === this.total
    );
  }

  get mostrarPregunta(): boolean {
    return this.total > 0 && !this.allNoRequiere && !this.allNoFactibleOCumple;
  }

  get botonSiguienteDeshabilitado(): boolean {
    if (this.readonly) return true;

    if (this.anuladoConfirmado) return true;
    if (this.allNoFactibleOCumple) return true;
    if (this.allNoRequiere) return false;

    return this.selectedDecision !== 'si';
  }

  get mensajeBloqueo(): string | null {
    if (this.allNoFactibleOCumple) {
      return 'Según las reglas de auditoría configuradas en el sistema, no es posible continuar el proceso de radicación. Para mayor detalle, visualice el resultado del proceso.';
    }
    return null;
  }

  resolverEstado(codigo: string): AuditoriaStatusConfig {
    return this.statusConfig[codigo as AuditoriaClasificacion];
  }

  trackByNumero(_index: number, item: InterfazAuditoriaResponse): number {
    return item.no;
  }

  seleccionarDecision(decision: 'si' | 'no'): void {
    if (this.readonly) {
      return;
    }

    this.selectedDecision = decision;
    this.decisionCambio.emit(decision);

    if (decision === 'no') {
      this.openConfirmNo();
    }
  }

  continuarPaso(): void {
    if (this.botonSiguienteDeshabilitado) return;
    this.continuarSiguiente.emit();
  }

  toggleTooltip(tooltip: MatTooltip, event: Event): void {
    event.stopPropagation();
    tooltip.toggle();
  }

  ordenarPor(columna: keyof InterfazAuditoriaResponse): void {
    if (this.sortColumn === columna) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = columna;
      this.sortDirection = 'asc';
    }
  }

  private openConfirmNo(): void {
    if (!this.confirmNoTemplate) return;

    const modalRef = this.modalService.open(ModalBasicComponent, {
      centered: true,
      windowClass: 'custom-modal-radius',
    });

    modalRef.componentInstance.title = 'Confirmación';
    modalRef.componentInstance.content = this.confirmNoTemplate;

    modalRef.componentInstance.buttons = [
      {
        text: 'No',
        color: 'btn-secondary',
        minWidth: 'none',
        action: 'close',
        value: true,
        callback: () => {
          modalRef.close();
          this.selectedDecision = null;
          this.decisionCambio.emit(null);
          this.anuladoConfirmado = false;
          this.anuladoMensaje = '';
          this.anuladoErrorMensaje = '';
        },
      },
      {
        text: 'Sí',
        color: 'btn-primary',
        minWidth: 'none',
        action: 'custom',
        value: true,
        callback: () => {
          modalRef.close();
          this.radicarAnulado();
        },
      },
    ];
  }

  private sortData(
    data: InterfazAuditoriaResponse[],
  ): InterfazAuditoriaResponse[] {
    if (!this.sortColumn) return data;
    return data.sort((a, b) => {
      const valA = a[this.sortColumn!];
      const valB = b[this.sortColumn!];
      if (valA == null || valB == null) return 0;
      const result = valA > valB ? 1 : valA < valB ? -1 : 0;
      return this.sortDirection === 'asc' ? result : -result;
    });
  }

  private radicarAnulado(): void {
    this.anuladoErrorMensaje = '';
    this.anuladoMensaje = '';

    if (this.simularErrorRadicado) {
      this.anuladoErrorMensaje =
        'Ocurrió un error inesperado al momento de guardar la solicitud en estado anulado. Intente nuevamente o contacte al administrador del sistema si la falla persiste.';
      this.anuladoConfirmado = false;
      return;
    }

    this.anuladoConfirmado = true;
    this.anuladoMensaje =
      'Solicitud radicada en estado anulado (demo). Número de solicitud: OV-000123. Al cerrar este mensaje, se recargarían los formularios (onReset) conservando los pasos previos según aplique.';
    this.anuladoExitoso.emit();
  }

  reportarErrorValidacion(): void {
    this.mensajeErrorValidacion =
      'Ha ocurrido un error al ejecutar la validación de auditoría. Por favor intente nuevamente. Si el problema persiste, contacte al administrador del sistema.';
  }

  reintentarAuditoria(): void {
    // UI-only: aquí se relanzaría el proceso sin perder datos de pasos 1-3
    this.mensajeErrorValidacion = '';
    this.selectedDecision = null;
    this.anuladoConfirmado = false;
  }
}
